import { collection, doc, getDoc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { round2 } from './lineMath';

// ── PAYMENT STATUS: ONE ENGINE, COMPUTED, NEVER READ BACK FROM STORAGE ────────
//
// Whether an invoice is paid is a pure function of two things: its grand total
// and the payment records in the `payments` collection that point at it. Every
// status, balance and credit the app shows is derived HERE, from those two
// inputs, at the moment it is shown. No screen works it out for itself.
//
// WHY. Invoices used to carry isPaid / isPartialPaid / paidAmount / totalPaid /
// balanceDue as stored fields, set when a payment was recorded and trusted from
// then on. Editing the invoice afterwards changed grandTotal but not those
// fields, so an invoice paid in full and then given a $200 delivery fee went on
// saying PAID IN FULL with nothing owed. A stored copy of a derived value goes
// stale the moment either input changes somewhere that forgot to update it.
// A value computed from its inputs cannot.
//
// CENT ROUNDING. Summing currency as floats leaves residue (100.00000000000001
// - 100.00), which once made an exactly-paid invoice read "PARTIAL — $0.00
// remaining". The total, the sum and the difference are all rounded to cents
// with the same round2 the totals math uses, so a balance can never disagree
// by a cent with the grand total it came from.

export const PAYMENT_STATUS = Object.freeze({
  UNPAID: 'Unpaid',
  PARTIAL: 'Partial',
  PAID: 'Paid',
  CREDIT: 'Credit',
});

// Sum of payment records, to the cent.
export function sumPayments(payments) {
  return round2((payments || []).reduce((s, p) => s + (parseFloat(p?.amount) || 0), 0));
}

// grandTotal: the invoice's total. payments: the payment RECORDS for it.
//
// Returns:
//   totalPaid     sum of the records
//   balanceDue    > 0 when money is owed, otherwise 0
//   creditAmount  > 0 when the customer paid more than the total, otherwise 0
//   status        'Unpaid' | 'Partial' | 'Paid' | 'Credit'
//
// balanceDue and creditAmount are never both above zero, and neither is ever
// negative. An overpayment is NOT rounded down to a zero balance: a customer
// who paid $150 too much has $150 with us, and creditAmount says so.
//
// 'Paid' needs money actually received. A $0 invoice with no payments is
// 'Unpaid' with nothing owed, exactly as it was before this engine existed.
export function getPaymentStatus(grandTotal, payments) {
  // The old signature took a number. Refuse one loudly rather than quietly
  // treating it as "no payments" and calling a paid invoice unpaid.
  if (!Array.isArray(payments)) {
    throw new TypeError('getPaymentStatus(grandTotal, payments): pass the payment records, not a total.');
  }
  const total = round2(grandTotal);
  const totalPaid = sumPayments(payments);
  const net = round2(total - totalPaid);
  const balanceDue = net > 0 ? net : 0;
  const creditAmount = net < 0 ? round2(-net) : 0;

  let status;
  if (creditAmount > 0) status = PAYMENT_STATUS.CREDIT;
  else if (balanceDue > 0) status = totalPaid > 0 ? PAYMENT_STATUS.PARTIAL : PAYMENT_STATUS.UNPAID;
  else status = totalPaid > 0 ? PAYMENT_STATUS.PAID : PAYMENT_STATUS.UNPAID;

  return { totalPaid, balanceDue, creditAmount, status };
}

// Nothing is owed and money was received: paid in full, or more than in full.
export const isSettled = (status) =>
  status === PAYMENT_STATUS.PAID || status === PAYMENT_STATUS.CREDIT;

// ── THE STORED MIRROR ─────────────────────────────────────────────────────────
//
// ⚠️ A DERIVED MIRROR. NEVER READ THESE FIELDS AS TRUTH.
//
// isPaid, isPartialPaid, totalPaid, paidAmount and balanceDue are still written
// onto the invoice document during the transition, for one reason: the
// Documents list reads them and has not been moved to the live computation yet
// (it does not load payment records — that is its own step). Everything else
// computes status with getPaymentStatus.
//
// Every writer rebuilds all five from scratch, from the payments collection,
// through this function — never "stored paidAmount + the new amount", which
// is a second way for the number to drift. When the last reader is gone,
// delete this function, syncInvoicePaymentMirror, and the fields.
//
// A credit is stored as isPaid: true with balanceDue 0; the credit itself is
// deliberately not stored, because that would be one more copy to go stale.
export function paymentMirrorFields(grandTotal, payments) {
  const { totalPaid, balanceDue, status } = getPaymentStatus(grandTotal, payments);
  return {
    isPaid: isSettled(status),
    isPartialPaid: status === PAYMENT_STATUS.PARTIAL,
    totalPaid,
    paidAmount: totalPaid,
    balanceDue,
  };
}

// ── READING PAYMENT RECORDS ───────────────────────────────────────────────────

const byCreatedAt = (a, b) => new Date(a.createdAt) - new Date(b.createdAt);

// All payment records for one invoice, oldest first. Throws if the read fails:
// callers must treat "could not read" as UNKNOWN, never as "no payments".
export async function fetchInvoicePayments(invoiceId) {
  const snap = await getDocs(query(collection(db, 'payments'), where('invoiceId', '==', invoiceId)));
  return snap.docs.map(d => ({ id: d.id, ...d.data() })).sort(byCreatedAt);
}

// Firestore caps an `in` filter at 30 values.
const IN_LIMIT = 30;

// Payment records for many invoices, as { invoiceId: [records] }. Every id asked
// for gets an entry, empty when it has no payments. Throws if any read fails.
// Single-field `in` filters with no orderBy, so no composite index is needed.
export async function fetchPaymentsForInvoices(invoiceIds) {
  const ids = [...new Set((invoiceIds || []).filter(Boolean))];
  const byInvoice = {};
  ids.forEach(id => { byInvoice[id] = []; });
  const chunks = [];
  for (let i = 0; i < ids.length; i += IN_LIMIT) chunks.push(ids.slice(i, i + IN_LIMIT));
  const snaps = await Promise.all(chunks.map(chunk =>
    getDocs(query(collection(db, 'payments'), where('invoiceId', 'in', chunk)))
  ));
  snaps.forEach(snap => snap.docs.forEach(d => {
    const p = { id: d.id, ...d.data() };
    if (byInvoice[p.invoiceId]) byInvoice[p.invoiceId].push(p);
  }));
  Object.values(byInvoice).forEach(list => list.sort(byCreatedAt));
  return byInvoice;
}

// ── REBUILDING THE MIRROR AFTER A PAYMENT WRITE ───────────────────────────────
//
// Called AFTER a payment record has been added or deleted. Reads the invoice's
// CURRENT grand total and EVERY payment record for it, derives the mirror, and
// writes it in one update.
//
// The grand total is re-read rather than taken from the screen: the screen's
// copy is from when the page opened, and the invoice may have been edited since.
//
// Not a transaction, because it cannot be one: a Firestore client transaction
// can only read documents by reference, not run a query, so the collection read
// cannot happen inside it. Two people recording payments on the same invoice at
// the same instant could therefore leave the mirror one payment behind until
// the next write — but every write re-reads the whole collection, so the next
// one always converges, and no screen reads the mirror as truth anyway.
//
//   paidAt  written only when the result is settled (Paid or Credit)
//   extra   other fields to write in the same update; they cannot override
//           the mirror fields
//
// Returns the status result (plus the records), or null if the invoice no
// longer exists. Throws if a read or the write fails.
export async function syncInvoicePaymentMirror(invoiceId, { paidAt, extra } = {}) {
  const ref = doc(db, 'invoices', invoiceId);
  const [invSnap, payments] = await Promise.all([getDoc(ref), fetchInvoicePayments(invoiceId)]);
  if (!invSnap.exists()) return null;
  const grandTotal = invSnap.data().grandTotal;
  const result = getPaymentStatus(grandTotal, payments);
  await updateDoc(ref, {
    ...(extra || {}),
    ...paymentMirrorFields(grandTotal, payments),
    ...(paidAt && isSettled(result.status) ? { paidAt } : {}),
  });
  return { ...result, payments };
}

// The message for when the money-side write succeeded (a payment recorded or
// deleted, an invoice created with payments) but the mirror rebuild after it
// failed. The first write is NOT undone, and the user must NOT be told to "try
// again" — that is exactly how the same payment gets entered twice.
export function mirrorSyncFailedMessage(whatHappened) {
  return `${whatHappened}\n\n`
    + "The invoice's stored payment summary could not be updated. The invoice screen and the customer profile read the payment records directly, so they show the correct status. "
    + 'The Documents list may show the old status until the next payment is recorded or the invoice is saved again.';
}
